import { useReducer, useEffect, useMemo, useRef } from 'react';
import scrollIntoView from 'scroll-into-view';

import { errorLogger, fetchJSON, wait, getHeaderHeight, uri } from 'utils';

import { searchValueIsChanged } from '../../FilteredListing/utils';

import types from '../types';
import reducer from '../store/reducer';
import {
    getFiltersNextStateForSingleCategoryDocs,
    getInitialState,
    getRequestBodyFromFilters,
    getResetedFilters,
    getActiveFilters,
    shouldShowPagination,
    setListingStateToURL,
    setFiltersCheckboxGroupState
} from '../utils';
import {
    initAction,
    setItemsAction,
    setFiltersAction,
    setSearchValueAction,
    setIsLoadingAction,
    setIsLoadingMoreAction,
    setCurrentPageAction,
    setPaginationAction
} from '../store/actions';
import { LISTING_FIRST_PAGE } from '../../FilteredListing/constants';

let abortController = null;

/**
 * @typedef Output
 * @type {Object}
 * @property {types.Filter[]} activeFilters
 * @property {types.Product[]} items
 * @property {types.Filter[]} filters
 * @property {types.Pagination} pagination
 * @property {string} paginationURLBase
 * @property {string} searchValue
 * @property {number} nextItemsAmount
 * @property {boolean} showPagination
 * @property {boolean} isLoading
 * @property {boolean} isLoadingMore
 * @property {MutableRefObject<HTMLDivElement>} scrollAnchorRef
 * @property {Function} handleShowAllClick
 * @property {Function} handlePageChange
 * @property {Function} handleLoadMoreCLick
 * @property {Function} handleSubmit
 * @property {Function} handleSearchInputChange
 * @property {Function} handleCheckboxChange
 * @property {Function} handleClearFilters
 */

/**
 * @typedef  Params
 * @type {Object}
 * @property {string} api
 * @property {string} culture
 * @property {string} siteName
 * @property {types.Filter[]} filters
 * @property {types.Pagination} pagination
 */

/**
 * @param {Params} initialData
 * @returns {Output}
 */
const useProductFinderAPI = initialData => {
    const { api, culture, siteName } = initialData;

    const [
        { items, filters, pagination, isLoading, isLoadingMore, searchValue },
        dispatch
    ] = useReducer(
        reducer,
        getInitialState({
            filters: initialData.filters || [],
            pagination: initialData.pagination || {},
            url: window.location.href
        })
    );

    const scrollAnchorRef = useRef(null);

    const activeFilters = useMemo(() => getActiveFilters(filters), [filters]);

    const scrollToAnchor = callback => {
        const scrollTarget = scrollAnchorRef && scrollAnchorRef.current;

        if (scrollTarget) {
            const topOffset = getHeaderHeight();

            scrollIntoView(
                scrollTarget,
                {
                    align: {
                        top: 0,
                        topOffset
                    }
                },
                callback
            );
        }
    };

    const paginationURLBase = useMemo(() => window.location.href, [
        filters,
        pagination
    ]);

    const showPagination = useMemo(
        () => shouldShowPagination(items, pagination),
        [items, pagination]
    );

    const nextItemsAmount = useMemo(() => {
        const { rowsCount, currentPage, rowsOnPage } = pagination;
        const remaining = rowsCount - currentPage * rowsOnPage;
        const limit = remaining > rowsOnPage ? rowsOnPage : remaining;

        return limit;
    }, [pagination]);

    const fetchData = (values, signal) =>
        fetchJSON({
            url: api,
            method: 'POST',
            body: JSON.stringify(values),
            signal
        });

    const handleFetchFilteredPageDataSuccess = (response, isMerging) => {
        dispatch(
            setItemsAction({
                items: response.payload.data,
                isMerging
            })
        );
        dispatch(setFiltersAction(response.payload.filters));
        dispatch(setPaginationAction(response.payload.pagination));

        // hide loader
        if (isMerging) {
            dispatch(setIsLoadingMoreAction(false));
        } else {
            dispatch(setIsLoadingAction(false));
        }
    };

    const handleFetchFilteredPageDataError = (error, isMerging) => {
        errorLogger(
            (error && error.data && error.data.message) ||
                `handleFetchFilteredPageDataError: ${error}`
        );

        // if we intentionally cancelled the request, we keep showing the loader
        // animation until the next fetch succeeds
        if (
            error &&
            error.originalError &&
            error.originalError.name === 'AbortError'
        ) {
            return;
        }

        // hide loader
        if (isMerging) {
            dispatch(setIsLoadingMoreAction(false));
        } else {
            dispatch(setIsLoadingAction(false));
        }
    };

    const fetchFilteredPageData = async ({
        isMerging = false,
        updateURL = true,
        page,
        nextFilters = filters,
        nextSearchValue = searchValue
    }) => {
        if (updateURL) {
            setListingStateToURL({
                page,
                filters: nextFilters,
                searchValue: nextSearchValue
            });
        }

        if (isMerging) {
            dispatch(setIsLoadingMoreAction(true));
        } else {
            dispatch(setIsLoadingAction(true));
        }

        // cancel previous request
        if (abortController) {
            abortController.abort();
        }

        // make request cancellable
        abortController = new AbortController();
        const { signal } = abortController;

        try {
            // show loading animation
            await wait();

            const response = await fetchData(
                {
                    ...getRequestBodyFromFilters(nextFilters),
                    pageSize: pagination.rowsOnPage,
                    page,
                    searchValue: nextSearchValue,
                    culture,
                    siteName
                },
                signal
            );

            handleFetchFilteredPageDataSuccess(response, isMerging);
        } catch (error) {
            handleFetchFilteredPageDataError(error, isMerging);
        }
    };

    const handleShowAllClick = (filterId, filterOptionId) => {
        const nextFilters = getFiltersNextStateForSingleCategoryDocs(
            filters,
            filterId,
            filterOptionId
        );

        dispatch(setFiltersAction(nextFilters));

        scrollToAnchor(() => {
            fetchFilteredPageData({
                page: LISTING_FIRST_PAGE,
                nextFilters
            });
        });
    };

    const handlePageChange = nextPage => {
        if (nextPage === pagination.currentPage) {
            return;
        }

        // better UX -> move viewport to the top of the page, so user knows
        // new page will arrive
        scrollToAnchor(() => {
            dispatch(setCurrentPageAction(nextPage));

            fetchFilteredPageData({ page: nextPage });
        });
    };

    const handleLoadMoreCLick = nextPage => {
        fetchFilteredPageData({ page: nextPage, isMerging: true });
    };

    const handleSearchInputChange = value => {
        dispatch(setSearchValueAction(value));
    };

    const handleSubmit = event => {
        event.preventDefault();
        if (!searchValueIsChanged(searchValue, window.location.href)) {
            return;
        }

        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE
        });
    };

    const handleCheckboxChange = (checkboxId, filterId) => {
        const nextFilters = setFiltersCheckboxGroupState({
            filters,
            id: checkboxId,
            filterId
        });

        dispatch(setFiltersAction(nextFilters));

        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextFilters
        });
    };

    const handleClearFilters = () => {
        const query = uri.getParams(window.location.href);
        const paramsKeys = Object.keys(query);

        if (paramsKeys.length === 0) {
            return;
        }

        const nextFilters = getResetedFilters(filters);

        dispatch(setFiltersAction(nextFilters));
        dispatch(setCurrentPageAction(LISTING_FIRST_PAGE));

        fetchFilteredPageData({
            page: LISTING_FIRST_PAGE,
            nextFilters
        });
    };

    const setStateByCurrentURL = () => {
        const nextState = getInitialState({
            advancedFilters: initialData.advancedFilters || [],
            items,
            pagination,
            filters,
            sorting,
            url: window.location.href
        });

        dispatch(initAction(nextState));

        fetchFilteredPageData({
            page: nextState.pagination.currentPage,
            nextFilters: nextState.filters,
            nextSearchValue: nextState.searchValue,
            updateURL: false
        });
    };

    const handlePopStateChange = () => setStateByCurrentURL();

    useEffect(() => {
        // listen for going back/forward in history
        const onPopStateListener = () => handlePopStateChange();

        window.addEventListener('popstate', onPopStateListener);

        return () => {
            if (onPopStateListener) {
                window.removeEventListener('popstate', onPopStateListener);
            }
        };
    }, [handlePopStateChange]);

    useEffect(() => {
        fetchFilteredPageData({ page: pagination.currentPage });
    }, []);

    return {
        activeFilters,
        items,
        filters,
        searchValue,
        pagination,
        paginationURLBase,
        nextItemsAmount,
        showPagination,
        isLoading,
        isLoadingMore,
        scrollAnchorRef,
        handleShowAllClick,
        handlePageChange,
        handleLoadMoreCLick,
        handleSubmit,
        handleSearchInputChange,
        handleCheckboxChange,
        handleClearFilters
    };
};

export default useProductFinderAPI;
